import Accordion from './Accordion.jsx';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BoundaryMap from './BoundaryMap.jsx';
import DateRangePicker from './DateRangePicker.jsx';
import DeleteIcon from '@mui/icons-material/Delete';
import Header from '../common/layout/Header.jsx';
import Notifier from '../common/Notifier.jsx';
import OrgAutocomplete from '../common/org-autocomplete/OrgAutocomplete.jsx';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import UserAutocomplete from './UserAutocomplete.jsx';
import isEqual from 'lodash/isEqual.js';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {DatePicker} from '@mui/x-date-pickers-pro';
import {DateTime} from 'luxon';
import {Link as RouterLink, useParams} from 'react-router-dom';
import {Routes} from '@account/utils/route';
import {
  useCreatePolicy,
  useGetPolicy,
  useUpdatePolicy,
} from '@account/api/archiveControl.js';
import {useGetCollection, useGetCollections} from '@account/api/features.js';
import {useGetItemTypes} from '@account/api/itemTypes.js';
import {useHistory} from 'react-router-dom/cjs/react-router-dom.min.js';

const haveToisChanged = (ranges1, ranges2) => {
  if (ranges1.length !== ranges2.length) {
    return true;
  }
  const hasChanged = ranges1.every(
    (range, index) =>
      range.start !== ranges2[index]?.start || range.end !== ranges2[index]?.end
  );
  return hasChanged;
};

const dateRangesToISO = ranges =>
  ranges.map(range => ({
    start: range.start?.toISO(),
    end: range.end?.toISO(),
  }));

const PolicyForm = () => {
  const {policyId} = useParams();
  const history = useHistory();

  const [name, setName] = useState('');
  const [collectionInputValue, setCollectionInputValue] = useState('');
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedItemTypes, setSelectedItemTypes] = useState([]);
  const [delay, setDelay] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [tois, setTois] = useState([]);

  const itemTypesQuery = useGetItemTypes();
  const featureCollectionsQuery = useGetCollections();
  const policyQuery = useGetPolicy(
    {policyId},
    {
      enabled: !!policyId,
    }
  );

  const policy = policyQuery?.data;

  const collectionQuery = useGetCollection(
    {collectionId: policy?.collectionId},
    {
      enabled: !!policy && !!policy?.collectionId,
    }
  );

  const updatePolicy = useUpdatePolicy({
    onSuccess: () => {
      history.push(`${Routes.ARCHIVE_CONTROL}/${policyId}`);
    },
  });
  const createPolicy = useCreatePolicy({
    onSuccess: data => {
      history.push(`${Routes.ARCHIVE_CONTROL}/${data.id}`);
    },
  });

  const formHasChanged =
    policy &&
    (policy?.name !== name ||
      !isEqual(policy?.body.item_types, selectedItemTypes) ||
      policy?.body.collection_ref !== selectedCollection?.['pl:ref'] ||
      selectedOrgs.length !== policy?.excluded_organization_ids.length ||
      selectedUsers.length !== policy?.excluded_user_ids.length ||
      delay !== policy.body.delay ||
      startDate?.toMillis() !==
        (policy.body.start_date
          ? DateTime.fromISO(policy.body.start_date, {zone: 'utc'}).toMillis()
          : undefined) ||
      haveToisChanged(dateRangesToISO(tois), policy.body.tois ?? []));

  const formHasRequiredFields =
    !!name &&
    selectedItemTypes.length > 0 &&
    !!selectedCollection &&
    (delay > 0 || !!startDate);

  const canSubmit = policyId
    ? formHasChanged && formHasRequiredFields
    : formHasRequiredFields;

  useEffect(() => {
    if (policy) {
      setName(policy.name);
      setSelectedItemTypes(policy.body.item_types);
      setSelectedOrgs(
        policy.excluded_organization_ids.map(orgId => ({id: orgId}))
      );
      setSelectedUsers(policy.excluded_user_ids?.map(userId => ({id: userId})));
      setDelay(policy.body.delay);
      if (policy.body.start_date) {
        setStartDate(DateTime.fromISO(policy.body.start_date, {zone: 'utc'}));
      }

      if (policy.body.tois?.length > 0) {
        const dateRanges = policy.body.tois.map(range => ({
          start: DateTime.fromISO(range.start, {zone: 'utc'}),
          end: range.end ? DateTime.fromISO(range.end, {zone: 'utc'}) : null,
        }));

        setTois(dateRanges);
      }
    }
  }, [policy]);

  useEffect(() => {
    if (collectionQuery?.data) {
      setSelectedCollection(collectionQuery?.data);
    }
  }, [collectionQuery?.data]);

  const handleSubmit = () => {
    // remove any incomplete ranges
    const toiRanges = tois.filter(range => !!range.start);

    const payloadBody = {
      collection_ref: selectedCollection['pl:ref'],
      delay: parseInt(delay, 10) ?? null,
      item_types: selectedItemTypes,
      start_date: startDate,
      toi_ranges: toiRanges,

      //backwards compat -- remove once teams migrate
      aois: [
        {
          type: 'Feature',
          geometry: {
            content: selectedCollection['pl:ref'],
            type: 'ref',
          },
        },
      ],
      delay_target: parseInt(delay, 10) ?? null,
      delay_ramp_effective_date: startDate,
    };

    const payload = {
      name,
      body: payloadBody,
      excluded_organization_ids: selectedOrgs.map(org => org.id),
      excluded_user_ids: selectedUsers.map(user => user.id),
    };

    if (policyId) {
      updatePolicy.mutate({id: policyId, ...payload});
    } else {
      createPolicy.mutate(payload);
    }
  };

  if (itemTypesQuery.isLoading || featureCollectionsQuery.isLoading) {
    return <Notifier open progress message="Loading..." />;
  }

  return (
    <Box sx={{paddingBlockEnd: 2}}>
      <Header name={`${policyId ? 'Edit' : 'Create'} Archive Control Policy`}>
        <Stack direction="row" sx={{gap: 1}}>
          <Button
            variant="outlined"
            color="primary"
            component={RouterLink}
            to={
              policyId
                ? `${Routes.ARCHIVE_CONTROL}/${policyId}`
                : Routes.ARCHIVE_CONTROL
            }
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            disabled={
              !canSubmit || updatePolicy.isPending || createPolicy.isPending
            }
            onClick={handleSubmit}
          >
            {policyId ? 'Save' : 'Create'}
          </Button>
        </Stack>
      </Header>
      <Stack>
        <TextField
          label="Name"
          required
          value={name}
          onChange={event => {
            setName(event.target.value);
          }}
        />

        <Accordion title="Item Types">
          <Typography variant="body2" sx={{marginBlockEnd: 2}}>
            Select item types to which this policy applies (data matching these
            types will be blocked)
          </Typography>
          <FormGroup row sx={{marginInlineStart: 1}}>
            {itemTypesQuery.data?.map(itemType => (
              <FormControlLabel
                key={itemType.id}
                control={
                  <Checkbox
                    checked={selectedItemTypes.includes(itemType.id)}
                    onChange={event => {
                      const {name, checked} = event.target;
                      if (checked) {
                        setSelectedItemTypes(prev => [...prev, name]);
                      } else {
                        setSelectedItemTypes(prev =>
                          prev.filter(p => p !== name)
                        );
                      }
                    }}
                    name={itemType.id}
                  />
                }
                label={itemType.display_name}
              />
            ))}
            <FormControlLabel
              key="mosaics"
              control={
                <Checkbox
                  checked={selectedItemTypes.includes('basemap')}
                  onChange={event => {
                    const {name, checked} = event.target;
                    if (checked) {
                      setSelectedItemTypes(prev => [...prev, name]);
                    } else {
                      setSelectedItemTypes(prev =>
                        prev.filter(p => p !== name)
                      );
                    }
                  }}
                  name="basemap" //backwards compat key
                />
              }
              label="Mosaics"
            />
          </FormGroup>
        </Accordion>

        <Accordion title="Exclusion times">
          <Typography variant="body2" sx={{marginBlockEnd: 2}}>
            You must specify at least one of these fields
          </Typography>
          <Stack direction="row" sx={{gap: 1}}>
            <TextField
              type="number"
              label="Delay (days)"
              helperText="Number of days to delay availability"
              value={delay}
              onChange={event => {
                const {value} = event.target;
                setDelay(value);
              }}
              inputProps={{
                min: 1,
                step: 1,
              }}
              onKeyDown={event => {
                if (
                  event.key === '.' ||
                  event.key === ',' ||
                  event.key === '-'
                ) {
                  event.preventDefault();
                }
              }}
            />
            <DatePicker
              timezone="utc"
              label="Start date"
              format="yyyy-MM-dd"
              slotProps={{
                textField: {
                  helperText: 'Cutoff start',
                },
                field: {clearable: true},
              }}
              value={startDate}
              onChange={date => {
                setStartDate(date ? date.startOf('day') : null);
              }}
              sx={{minWidth: 300}}
            />
          </Stack>
          <Divider sx={{marginBlock: 2}} />
          <Stack sx={{gap: 2}}>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="body2">
                Optional: Add a date range
              </Typography>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  setTois(prev => [
                    ...prev,
                    {
                      start: null,
                      end: null,
                    },
                  ]);
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Stack>
            {tois.map((toiRange, index) => (
              <Stack
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                direction="row"
                sx={{gap: 1, alignItems: 'center', width: '100%'}}
              >
                <DateRangePicker
                  startDate={toiRange.start}
                  onChangeStartDate={date => {
                    setTois(prev => {
                      const current = prev[index];
                      current.start = date;
                      return [...prev];
                    });
                  }}
                  endDate={toiRange.end}
                  onChangeEndDate={date => {
                    setTois(prev => {
                      const current = prev[index];
                      current.end = date;
                      return [...prev];
                    });
                  }}
                />
                <Tooltip text="Remove range">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setTois(prev => prev.filter((_, idx) => index !== idx));
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </Accordion>

        <Accordion title="Exclusion Area">
          <Typography variant="body2" sx={{marginBlockEnd: 2}}>
            The area that will be blocked from access.
          </Typography>
          <Stack sx={{gap: 2}}>
            <Autocomplete
              options={featureCollectionsQuery.data?.collections}
              loading={featureCollectionsQuery.isLoading}
              sx={{width: '50%'}}
              getOptionLabel={option => option.title ?? ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={params => (
                <TextField {...params} label="Choose a boundary" />
              )}
              value={selectedCollection}
              onChange={(event, newValue) => {
                setSelectedCollection(newValue);
              }}
              inputValue={collectionInputValue}
              onInputChange={(_, newValue) => {
                setCollectionInputValue(newValue);
              }}
            />
            <Box sx={{height: 350}}>
              <BoundaryMap collection={selectedCollection} />
            </Box>
          </Stack>
        </Accordion>

        <Accordion title="Exempt Organizations" defaultExpanded={!!policyId}>
          <Stack sx={{gap: 2, overflow: 'hidden'}}>
            <Typography variant="body2">
              Organizations below are exempt from this policy (not blocked).
            </Typography>
            <OrgAutocomplete
              multiple
              freeSolo
              disableClearable
              filterOptions={options =>
                options.filter(o => !selectedOrgs.some(s => s.id === o.id))
              }
              showCreateOrgLink={false}
              getOptionKey={option => option.id}
              placeholder="Search for an organization by name or ID"
              onChange={orgs => {
                const newValue = orgs.map(org => ({
                  ...org,
                  new: !selectedOrgs.find(s => s.id === org.id),
                }));
                setSelectedOrgs(newValue.reverse());
              }}
              renderTags={() => null}
              value={selectedOrgs}
            />
            <Stack
              direction="row"
              sx={{
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                maxHeight: 250,
                overflow: 'auto',
              }}
            >
              {selectedOrgs.map(org => (
                <Chip
                  shape="square"
                  key={org.id}
                  label={org.id}
                  color={org.new ? 'primary' : 'default'}
                  onDelete={() => {
                    setSelectedOrgs(prev =>
                      prev.filter(prevOrg => prevOrg.id !== org.id)
                    );
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Accordion>

        <Accordion title="Exempt Users" defaultExpanded={!!policyId}>
          <Stack sx={{gap: 2, overflow: 'hidden'}}>
            <Typography variant="body2" sx={{marginBlockEnd: 2}}>
              Users below are exempt from this policy (not blocked).
            </Typography>
            <UserAutocomplete
              multiple
              freeSolo
              disableClearable
              filterOptions={options =>
                options.filter(o => !selectedUsers.some(s => s.id === o.id))
              }
              onChange={orgs => {
                const newValue = orgs.map(org => ({
                  ...org,
                  new: !selectedUsers.find(s => s.id === org.id),
                }));
                setSelectedUsers(newValue.reverse());
              }}
              renderTags={() => null}
              value={selectedUsers}
            />
            <Stack
              direction="row"
              sx={{
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                maxHeight: 250,
                overflow: 'auto',
              }}
            >
              {selectedUsers.map(user => (
                <Chip
                  shape="square"
                  label={user.id}
                  key={user.id}
                  color={user.new ? 'primary' : 'default'}
                  onDelete={() => {
                    setSelectedUsers(prev =>
                      prev.filter(prevOrg => prevOrg.id !== user.id)
                    );
                  }}
                />
              ))}
            </Stack>
          </Stack>
        </Accordion>
      </Stack>
      <Notifier
        open={!!updatePolicy.isPending}
        progress
        message="Updating policy..."
      />
      <Notifier
        open={!!createPolicy.isPending}
        progress
        message="Creating policy..."
      />
      <Notifier
        open={!!policyQuery.isError}
        severity="error"
        message="There was an error fetching policy data"
      />
    </Box>
  );
};

PolicyForm.propTypes = {
  policy: PropTypes.object,
};

export default PolicyForm;
